import i18n from '@/utils/i18n';
import React, { useEffect, useRef, useState } from 'react';
import { Button, IconButton, Icons, SidePanel } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { Platform, TextInput, TextInputProps, View } from 'react-native';
import {
  PRICE_INPUT_REGEX,
  stripCommas,
} from '@/domain/pricing/utils/pricing-utils';
import { QuoteLineItem } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/hooks/use-quote-state-types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const PANEL_WIDTH = 400;

/** Fields the panel collects.
 *
 * `localId`, `sortOrder`, and `currencyCode` are assigned by the reducer,
 * not the caller, so they are omitted from the submitted payload. */
type CustomItemSubmitValue = Omit<
  QuoteLineItem,
  'currencyCode' | 'localId' | 'sortOrder'
>;

interface CustomItemPanelProps {
  onAddItem: (item: CustomItemSubmitValue) => void;
  onClose: () => void;
}

const EMPTY_FORM = {
  description: '',
  itemCode: '',
  itemName: '',
  packSize: '',
  price: '',
};

interface CustomItemFieldProps {
  inputRef?: React.Ref<TextInput>;
  keyboardType?: TextInputProps['keyboardType'];
  label: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  value: string;
}

/**
 * Label + single-line text input pair used by the custom item form. The
 * label doubles as the input's `accessibilityLabel` so tests (and screen
 * readers) can find the input via its field name without traversing the
 * DOM. The visual "*" on required fields is presentational only and is
 * omitted from the a11y label.
 */
const CustomItemField: React.FC<CustomItemFieldProps> = ({
  inputRef,
  keyboardType,
  label,
  onChangeText,
  placeholder,
  required,
  value,
}) => {
  const [styles, theme] = useStyles();
  return (
    <View style={styles.field}>
      <Typography
        color={theme.sk.color.text.secondary}
        variant="paragraph-md-600"
      >
        {required ? `${label} *` : label}
      </Typography>
      <TextInput
        accessibilityLabel={label}
        keyboardType={keyboardType}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={theme.sk.color.text.tertiary}
        ref={inputRef}
        style={styles.input}
        value={value}
      />
    </View>
  );
};

/**
 * Side-panel form for adding a custom (non-catalog) line item to a price
 * quote. Uses sk-ui `SidePanel`, which is non-modal and owns its own
 * slide-in/out animation when the caller mounts/unmounts it inside an
 * `<AnimatePresence>` wrapper.
 *
 * Validates that `itemName` is non-empty and `price` parses as a number; on
 * submit calls `onAddItem` with the collected fields and `onClose` to
 * dismiss. The form resets on unmount, so callers don't need to reset state
 * explicitly.
 *
 * The reducer (`ADD_CUSTOM_ITEM`) assigns the item's `localId` and
 * `sortOrder`, so callers don't need to provide them.
 */
export const CustomItemPanel: React.FC<CustomItemPanelProps> = ({
  onAddItem,
  onClose,
}) => {
  const [styles, theme] = useStyles();
  // used to ensure the close button of the side panel is tappable on ios
  const insets = useSafeAreaInsets();
  const [form, setForm] = useState(EMPTY_FORM);
  const itemNameRef = useRef<TextInput>(null);

  // Auto-focus the first required field on mount. Defer one frame so
  // focus runs after the SidePanel's slide-in animation has begun and
  // AnimatePresence has finished mounting — calling focus during the
  // mount commit was machine-dependent (some browsers refuse to focus
  // mid-transition). preventScroll keeps `element.focus()` from
  // horizontally scrolling the page since the SidePanel sits at the
  // right edge of the viewport.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const node = itemNameRef.current as
        | (TextInput & { focus: (opts?: { preventScroll?: boolean }) => void })
        | null;
      node?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const updateField = (field: keyof typeof EMPTY_FORM, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isValid =
    form.itemName.trim() !== '' &&
    form.price.trim() !== '' &&
    !isNaN(parseFloat(form.price));

  const handleAdd = () => {
    if (!isValid) {
      return;
    }
    onAddItem({
      isCatalogItemRemoved: false,
      itemCode: form.itemCode || undefined,
      itemDescription: form.description || undefined,
      itemName: form.itemName.trim(),
      itemPackSize: form.packSize.trim() || undefined,
      ourPrice: form.price.trim(),
      quantity: '1',
    });
    onClose();
  };

  return (
    <SidePanel
      closeOnEscape
      closeOnOutsidePress
      onClose={onClose}
      width={PANEL_WIDTH}
    >
      {/* Wrap SidePanel.Header in a View so we can push it below the iOS
       *  safe-area inset — SidePanel sits at top: 0 of its parent and the
       *  header would otherwise tuck under the iPad status bar. */}
      <View
        style={Platform.OS !== 'web' ? { paddingTop: insets.top } : undefined}
      >
        <SidePanel.Header
          leadingActions={
            <IconButton
              aria-label={i18n.t('close')}
              icon={<Icons.X color={theme.sk.color.icon.primary} />}
              onPress={onClose}
              shape="rounded"
              size="$sm"
              variant="tertiary"
            />
          }
          title={
            <View>
              <Typography
                color={theme.sk.color.text.primary}
                variant="heading-md"
              >
                {i18n.t('sales_portal.price_quotes.custom_item.title')}
              </Typography>
              <Typography
                color={theme.sk.color.text.secondary}
                variant="paragraph-md"
              >
                {i18n.t('sales_portal.price_quotes.custom_item.subtitle')}
              </Typography>
            </View>
          }
        />
      </View>

      <SidePanel.Body>
        <View style={styles.form}>
          <CustomItemField
            label={i18n.t('sales_portal.price_quotes.custom_item.item_code')}
            onChangeText={(v) => updateField('itemCode', v)}
            value={form.itemCode}
          />
          <CustomItemField
            inputRef={itemNameRef}
            label={i18n.t('sales_portal.price_quotes.custom_item.item_name')}
            onChangeText={(v) => updateField('itemName', v)}
            required
            value={form.itemName}
          />
          <CustomItemField
            label={i18n.t('sales_portal.price_quotes.custom_item.description')}
            onChangeText={(v) => updateField('description', v)}
            value={form.description}
          />
          <CustomItemField
            label={i18n.t('sales_portal.price_quotes.custom_item.pack_size')}
            onChangeText={(v) => updateField('packSize', v)}
            value={form.packSize}
          />
          <CustomItemField
            keyboardType="decimal-pad"
            label={i18n.t('sales_portal.price_quotes.custom_item.price')}
            onChangeText={(v) => {
              if (v && !PRICE_INPUT_REGEX.test(v)) {
                return;
              }
              updateField('price', stripCommas(v));
            }}
            placeholder="0.00"
            required
            value={form.price}
          />
        </View>
      </SidePanel.Body>

      <SidePanel.Footer>
        <Button
          accessibilityLabel={i18n.t(
            'sales_portal.price_quotes.custom_item.add_button',
          )}
          disabled={!isValid}
          onPress={handleAdd}
          size="$md"
          variant="primary"
          width="100%"
        >
          {i18n.t('sales_portal.price_quotes.custom_item.add_button')}
        </Button>
      </SidePanel.Footer>
    </SidePanel>
  );
};

const useStyles = createStyles((theme) => ({
  field: {
    gap: theme.sk.spacing.spaceXsmall,
  },
  form: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
  },
  input: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    color: theme.sk.color.text.primary,
    fontFamily: theme.sk.typography.paragraph.md.fontFamily,
    fontSize: theme.sk.typography.paragraph.md.fontSize,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
}));
